"use client";

import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { QueueContextShape } from "@packages/sdk";
import { snakeCase } from "@packages/sdk";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { getContentId } from "../lib/contentIdentification";
import type { MediaElementsRef } from "../types/MediaElementsRef.types";
import { useCurrentQueueItem } from "./useCurrentQueueItem";
import { usePlayer } from "./usePlayer";

export type UseMediaAnalyticsProps = {
  mediaRef?: RefObject<MediaElementsRef>;
  backgroundTitle?: string;
};

type SimpleAnalyticsFunction = () => Promise<void>;

type MediaAnalytics = {
  audioStartDelayed: SimpleAnalyticsFunction;
  audioStarted: SimpleAnalyticsFunction;
  changedSpeed: (speed: number) => Promise<void>;
  draggedTimeSlider: SimpleAnalyticsFunction;
  loopChanged: (loop: "none" | "one" | "all") => Promise<void>;
  maximizedPlayer: SimpleAnalyticsFunction;
  mediaAnalyticsProps: Record<string, any>;
  minimizedPlayer: SimpleAnalyticsFunction;
  playerBeganTrack: SimpleAnalyticsFunction;
  playerEndedTrack: (
    cause: "complete" | "error" | "cancel" | "timer",
  ) => Promise<void>;
  playerInitialized: SimpleAnalyticsFunction;
  refBasedProps: () => Record<string, any>;
  tappedFastForward: SimpleAnalyticsFunction;
  tappedPause: (player_state: "maximized" | "minimized") => Promise<void>;
  tappedPlay: (player_state: "maximized" | "minimized") => Promise<void>;
  tappedRestart: SimpleAnalyticsFunction;
  tappedResumeSession: SimpleAnalyticsFunction;
  tappedRewind: SimpleAnalyticsFunction;
  tappedSkipBack: SimpleAnalyticsFunction;
  tappedSkipForward: SimpleAnalyticsFunction;
  tappedSkipIntro: SimpleAnalyticsFunction;
  timerEnded: (timerLength: string) => Promise<void>;
};

const getAspectRatio = (width: number, height: number) => {
  function gcd(a: number, b: number): number {
    return b === 0 ? a : gcd(b, a % b);
  }
  const ratioGCD = gcd(width, height);
  return `${width / ratioGCD}:${height / ratioGCD}`;
};

export const useMediaAnalytics = ({
  mediaRef,
  backgroundTitle,
}: UseMediaAnalyticsProps = {}): RefObject<MediaAnalytics> => {
  const analytics = useAnalytics();
  const { currentItem, queue } = useCurrentQueueItem();
  const { subtitleIdx } = usePlayer();
  const returnRef = useRef<MediaAnalytics | null>(null);
  // Audio ID of the last track we fired PlayerBeganTrack for, cleared by PlayerEndedTrack
  const lastBeganAudioId = useRef<string | number | null>(null);

  const mediaAnalyticsProps = useMemo<Record<string, any>>(
    () => ({
      audio: currentItem?.selected_audio?.id,
      background_music: backgroundTitle ? snakeCase(backgroundTitle) : "none",
      content_id: getContentId(currentItem),
      content_type: currentItem?.type,
      downloaded: false,
      guides: currentItem?.selected_audio?.guide_ids,
      has_transcript: currentItem?.selected_audio?.has_transcript,
      id: currentItem?.selected_audio?.id,
      length: currentItem
        ? snakeCase(currentItem.selected_audio.length)
        : undefined,
      loop: queue?.repeating,
      online: true,
      shuffle: queue?.shuffled,
      subtitles_enabled: subtitleIdx !== null && subtitleIdx !== undefined,
    }),
    [currentItem, queue, subtitleIdx],
  );

  const refBasedProps = useCallback(
    () => ({
      aspect_ratio:
        currentItem?.selected_audio?.media_type === "video" &&
        mediaRef?.current &&
        mediaRef.current.width > 0
          ? getAspectRatio(mediaRef.current.width, mediaRef.current.height)
          : undefined,
      percent_complete: mediaRef?.current
        ? mediaRef.current.currentTime / mediaRef.current.duration
        : undefined,
      speed: mediaRef?.current ? mediaRef.current.playbackRate : 1,
      video_dimensions:
        currentItem?.selected_audio?.media_type === "video" &&
        mediaRef?.current &&
        mediaRef.current.width > 0
          ? `${mediaRef.current.width}x${mediaRef.current.height}`
          : undefined,
    }),
    [currentItem],
  );

  const playerEndedTrack = (
    cause: "complete" | "error" | "cancel" | "timer",
  ) => {
    lastBeganAudioId.current = null;
    return analytics.track({
      event: HallowAnalyticsEvent.PlayerEndedTrack,
      properties: {
        cause,
        ...mediaAnalyticsProps,
        ...refBasedProps(),
      },
    });
  };

  const playerInitialized = () =>
    analytics.track({
      event: HallowAnalyticsEvent.PlayerInitialized,
    });

  const audioStarted = () =>
    analytics.track({
      event: HallowAnalyticsEvent.AudioStarted,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const audioStartDelayed = () =>
    analytics.track({
      event: HallowAnalyticsEvent.AudioStartDelayed,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const changedSpeed = (s: number) =>
    analytics.track({
      event: HallowAnalyticsEvent.SpeedChanged,
      properties: { ...mediaAnalyticsProps, ...refBasedProps(), speed: s },
    });

  const draggedTimeSlider = () =>
    analytics.track({
      event: HallowAnalyticsEvent.DraggedTimeSlider,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedFastForward = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedFastForward,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedRestart = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedRestartSession,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedRewind = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedRewind,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedSkipForward = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipForward,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedSkipBack = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipBack,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const loopChanged = (loop: QueueContextShape["repeating"]) =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedLoop,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        loop,
      },
    });

  const tappedSkipIntro = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedSkipIntro,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        collection:
          currentItem?.type === "prayer" ? currentItem.collection.id : null,
      },
    });

  const tappedResumeSession = () =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedResumeSession,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        resumed_at: currentItem.selected_audio.resumes_at,
      },
    });

  const playerBeganTrack = async () => {
    const audioId = currentItem?.selected_audio?.id ?? null;
    // canplaythrough also fires after seeks/skips; only track once until reset
    if (audioId !== null && lastBeganAudioId.current === audioId) return;
    lastBeganAudioId.current = audioId;
    await analytics.track({
      event: HallowAnalyticsEvent.PlayerBeganTrack,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        collection:
          currentItem.type === "prayer" ? currentItem.collection.id : null,
      },
    });
  };

  const maximizedPlayer = () =>
    analytics.track({
      event: HallowAnalyticsEvent.MaximizedPlayer,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const minimizedPlayer = () =>
    analytics.track({
      event: HallowAnalyticsEvent.MinimizedPlayer,
      properties: { ...mediaAnalyticsProps, ...refBasedProps() },
    });

  const tappedPause = (player_state: "maximized" | "minimized") =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedPause,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        player_state,
      },
    });

  const tappedPlay = (player_state: "maximized" | "minimized") =>
    analytics.track({
      event: HallowAnalyticsEvent.TappedPlay,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        player_state,
      },
    });

  const timerEnded = (timerLength: string) =>
    analytics.track({
      event: HallowAnalyticsEvent.TimerEnded,
      properties: {
        ...mediaAnalyticsProps,
        ...refBasedProps(),
        length: snakeCase(timerLength),
      },
    });

  useEffect(() => {
    returnRef.current = {
      audioStartDelayed,
      audioStarted,
      changedSpeed,
      draggedTimeSlider,
      loopChanged,
      maximizedPlayer,
      mediaAnalyticsProps,
      minimizedPlayer,
      playerBeganTrack,
      playerEndedTrack,
      playerInitialized,
      refBasedProps,
      tappedFastForward,
      tappedPause,
      tappedPlay,
      tappedRestart,
      tappedResumeSession,
      tappedRewind,
      tappedSkipBack,
      tappedSkipForward,
      tappedSkipIntro,
      timerEnded,
    };
  }, [
    analytics?.track,
    audioStarted,
    audioStartDelayed,
    changedSpeed,
    draggedTimeSlider,
    loopChanged,
    maximizedPlayer,
    mediaAnalyticsProps,
    minimizedPlayer,
    playerBeganTrack,
    playerEndedTrack,
    playerInitialized,
    refBasedProps(),
    tappedFastForward,
    tappedPause,
    tappedPlay,
    tappedRestart,
    tappedResumeSession,
    tappedRewind,
    tappedSkipBack,
    tappedSkipForward,
    tappedSkipIntro,
    timerEnded,
  ]);

  return returnRef;
};
